// Contact-label membership is a SET: a chat is either in a label or it isn't.
// The DB, however, has accumulated duplicate `contacts-labels` rows for the same
// (labelId, waChatId) pair — up to 12× for a single chat on the "inbox" label —
// because the auto-add path (`handleNewIncomingWaMessageThunk`) read a stale
// redux snapshot and the historical @lid→@c.us migration re-pointed rows onto an
// id that already had a row.
//
// The visible symptom: "Remove from Inbox" issued one DELETE per click, so a chat
// with N rows only left the filtered list on the Nth click, while "Needs Attention"
// (which happens to have almost no duplicates) cleared in one.
//
// Toggling OFF must therefore remove EVERY row for the pair, not just the first
// one the selector happens to return.

export type ContactLabelRowLike = {
    _id?: string;
    labelId?: string;
    waChatId?: string;
    deleted?: boolean;
};

/**
 * All live rows binding `waChatId` to `labelId`. Returns every duplicate so the
 * caller can delete the whole set in one toggle.
 */
export function selectContactLabelRowsForPair<T extends ContactLabelRowLike>(
    contactLabels: T[] | undefined,
    args: { labelId: string; waChatId: string },
): T[] {
    const { labelId, waChatId } = args;
    if (!contactLabels?.length || !labelId || !waChatId) return [];
    return contactLabels.filter((row) =>
        row?.deleted !== true &&
        row?.labelId === labelId &&
        row?.waChatId === waChatId,
    );
}

/** Ids of every row to delete when a chat is toggled OUT of a label. */
export function selectContactLabelIdsToRemove(
    contactLabels: ContactLabelRowLike[] | undefined,
    args: { labelId: string; waChatId: string },
): string[] {
    return selectContactLabelRowsForPair(contactLabels, args)
        .map((row) => row._id)
        .filter((id): id is string => Boolean(id));
}
